import { MNSproCloudApi } from '../..';
import {
  AbsenceReasonEntry,
  AbsenceReasonPutEntry,
  AbsenceReasonResponse,
  ClassBasicResponse,
  ClassUpdateEntry,
  CourseBasicResponse,
  CourseCreateEntry,
  CourseOccurrence as CourseOccurrenceAPI,
  CourseResponse,
  CourseUpdateEntry,
  EditAndNotificationSettingsItem,
  ExcuseDefinitionResponse,
  GeneralSettingsItem,
  GradeUpdateEntry,
  Person,
  SchoolContact,
  SignOffRequirement,
} from '../../api/v2';
import {
  ClassCourse,
  Grade,
  GradeClass,
  SchoolManagement,
} from './classbookSettings.interfaces';
import {
  classBasicResponseToGradeClass,
  classResponseToGradeClass,
  courseResponseToClassCourse,
  dayToDayOfWeek,
  gradeBasicResponseToGrade,
  gradeResponseToGrade,
} from './ClassbookSettingsService.utils';

export const ClassbookSettingsService = {
  async getContactSettings(
    authToken: string,
    organizationId: string,
  ): Promise<SchoolContact> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);

    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getSchoolData(organizationId, undefined, {
      validateStatus: (status: number) =>
        (status >= 200 && status <= 299) || status === 404,
    });

    if (result.status === 404) {
      return {} as SchoolContact;
    }

    return result.data;
  },

  async saveContact(
    authToken: string,
    organizationId: string,
    schoolContact: SchoolContact,
  ): Promise<SchoolContact> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.createOrUpdateSchoolData(
      organizationId,
      schoolContact,
    );
    return result.data;
  },

  async getAbsenceControlSettings(
    authToken: string,
    organizationId: string,
  ): Promise<EditAndNotificationSettingsItem> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);

    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getEditAndNotificationSettings(
      organizationId,
      undefined,
      {
        validateStatus: (status: number) =>
          (status >= 200 && status <= 299) || status === 404,
      },
    );

    if (result.status === 404) {
      return {} as EditAndNotificationSettingsItem;
    }

    return result.data;
  },

  async hasAnyExistingAbsences(
    authToken: string,
    organizationId: string,
  ): Promise<boolean> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const response = await api.getAbsencesCount(organizationId);
    return (
      response.data.count !== undefined &&
      response.data.count > 0
    );
  },

  async saveAbsenceControlSettings(
    authToken: string,
    organizationId: string,
    editAndNotificationSettingsItem: EditAndNotificationSettingsItem,
  ): Promise<EditAndNotificationSettingsItem> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);

    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.createOrUpdateEditAndNotificationSettings(
      organizationId,
      editAndNotificationSettingsItem,
    );

    return result.data;
  },

  async getManagement(
    authToken: string,
    organizationId: string,
  ): Promise<SchoolManagement> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const managersResponse = await api.getSchoolManagers(
      organizationId,
      undefined,
      {
        validateStatus: (status: number) =>
          (status >= 200 && status <= 299) || status === 404,
      },
    );

    let management: Person[] = [];

    if (managersResponse.status === 200) {
      management = managersResponse.data.map<Person>(person => ({
        id: person.id,
        firstName: person.firstName,
        lastName: person.lastName,
        mailAddress: person.mailAddress || '',
        displayName: person.displayName || '',
      }));
    }

    let headTeachers: Person[] = [];

    const headTeachersResponse = await api.getSchoolHeadTeachers(
      organizationId,
      undefined,
      {
        validateStatus: (status: number) =>
          (status >= 200 && status <= 299) || status === 404,
      },
    );

    if (headTeachersResponse.status === 200) {
      headTeachers = headTeachersResponse.data.map<Person>(person => ({
        id: person.id,
        firstName: person.firstName,
        lastName: person.lastName,
        mailAddress: person.mailAddress || '',
        displayName: person.displayName || '',
      }));
    }

    return {
      headTeachers,
      management,
    };
  },

  async saveManagement(
    authToken: string,
    organizationId: string,
    headTeacherIds: string[],
    managerIds: string[],
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    await api.createOrUpdateSchoolHeadTeachers(organizationId, headTeacherIds);
    await api.createOrUpdateSchoolManagers(organizationId, managerIds);
  },

  async getGrades(authToken: string, organizationId: string): Promise<Grade[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicGradesOfSettings(organizationId);

    return result.data.value.map(gradeBasicResponseToGrade);
  },

  async createGrade(
    authToken: string,
    grade: Grade,
    organizationId: string,
  ): Promise<Grade> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.createGrade(organizationId, {
      azureId: grade.azureGroup?.id,
      name: grade.name,
      settings: {
        gradeManagers: grade.settings.gradeManagers.map(m => m.id),
        signOffRequirement: grade.settings
          ?.signOffRequirement as unknown as SignOffRequirement,
      },
    });

    return gradeResponseToGrade(result.data);
  },

  async updateGrade(
    authToken: string,
    grade: Grade,
    organizationId: string,
  ): Promise<Grade> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateGrade(organizationId, {
      id: grade.id,
      azureId: grade.azureGroup?.id,
      name: grade.name,
      settings: {
        gradeManagers: grade.settings.gradeManagers.map(m => m.id),
        signOffRequirement: grade.settings
          .signOffRequirement as unknown as SignOffRequirement,
      },
    });

    return gradeResponseToGrade(result.data);
  },

  async updateGradeFromGradeUpdateEntry(
    authToken: string,
    organizationId: string,
    gradeUpdateEntry: GradeUpdateEntry,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateGrade(organizationId, gradeUpdateEntry);
    return result;
  },

  async deleteGrade(
    authToken: string,
    gradeId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    await api.deleteGrade(gradeId, organizationId);
  },

  async getGrade(
    authToken: string,
    gradeId: string,
    organizationId: string,
  ): Promise<Grade> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestGradeWithBasicClassesOfSettings(
      gradeId,
      organizationId,
    );

    return gradeResponseToGrade(result.data);
  },

  async getGradeClasses(
    authToken: string,
    organizationId: string,
    gradeId: string,
  ): Promise<GradeClass[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestGradeWithBasicClassesOfSettings(
      gradeId,
      organizationId,
    );

    return result.data.gradeClasses?.map(classBasicResponseToGradeClass) ?? [];
  },

  async getGradeClassById(
    authToken: string,
    organizationId: string,
    classId: string,
  ): Promise<GradeClass> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestClassWithBasicCoursesOfSettings(
      classId,
      organizationId,
    );

    return classResponseToGradeClass(result.data);
  },

  async createGradeClass(
    authToken: string,
    organizationId: string,
    gradeId: string,
    gradeClass: GradeClass,
  ): Promise<GradeClass> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.createClass(gradeId, organizationId, {
      azureId: gradeClass.azureGroup?.id,
      name: gradeClass.name,
      classTeachers: gradeClass.classTeachers.map(t => t.id),
      signOffRequirement:
        gradeClass.signOffRequirement as unknown as SignOffRequirement,
    });

    return classResponseToGradeClass(result.data);
  },

  async updateGradeClass(
    authToken: string,
    organizationId: string,
    gradeId: string,
    gradeClass: GradeClass,
  ): Promise<GradeClass> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateClass(gradeId, organizationId, {
      id: gradeClass.id,
      azureId: gradeClass.azureGroup?.id,
      name: gradeClass.name,
      classTeachers: gradeClass.classTeachers.map(t => t.id),
      signOffRequirement:
        gradeClass.signOffRequirement as unknown as SignOffRequirement,
    });

    return classResponseToGradeClass(result.data);
  },

  async updateGradeClassFromClassUpdateEntry(
    authToken: string,
    organizationId: string,
    gradeId: string,
    classUpdateEntry: ClassUpdateEntry,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateClass(
      gradeId,
      organizationId,
      classUpdateEntry,
    );

    return result;
  },

  async deleteGradeClass(
    authToken: string,
    organizationId: string,
    classId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    /// swagger wrong deleteCourse returns string
    await api.deleteClass(classId, organizationId);
  },

  async getClassCourses(
    authToken: string,
    organizationId: string,
    classId: string,
  ): Promise<ClassCourse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestClassWithBasicCoursesOfSettings(
      classId,
      organizationId,
    );

    const flatCourses = result.data.classCourses ?? [];
    const coursesPromises = flatCourses?.map(c =>
      api.getLatestCourseOfSettings(c.id ?? '', organizationId),
    );

    const courses = (await Promise.all(coursesPromises)).map(p => p.data);
    return courses.map(courseResponseToClassCourse);
  },

  /**
   * Returns a single course with its occurrences, teachers and color.
   */
  async getCourse(
    authToken: string,
    organizationId: string,
    courseId: string,
  ): Promise<ClassCourse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getLatestCourseOfSettings(courseId, organizationId);

    return courseResponseToClassCourse(result.data);
  },

  async updateClassCourse(
    authToken: string,
    organizationId: string,
    course: ClassCourse,
  ): Promise<ClassCourse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateCourse(organizationId, {
      classIds: course.classes.map(cl => cl.id),
      id: course.id,
      azureId: course.azureGroup?.id,
      name: course.name,
      courseTeachers: course.courseTeachers.map(t => t.id),
      color: course.color,
      occurrences: course.occurrences?.map(
        o =>
          ({
            slot: o.slot,
            weekday: dayToDayOfWeek(o.weekday),
          } as CourseOccurrenceAPI),
      ),
    });

    return courseResponseToClassCourse(result.data);
  },

  async updateClassCourseFromCourseUpdateEntry(
    authToken: string,
    organizationId: string,
    courseUpdateEntry: CourseUpdateEntry,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateCourse(organizationId, courseUpdateEntry);
    return result.data;
  },

  async createClassCourse(
    authToken: string,
    organizationId: string,
    course: ClassCourse,
  ): Promise<ClassCourse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.createCourse(organizationId, {
      classIds: course.classes.map(cl => cl.id),
      azureId: course.azureGroup?.id,
      name: course.name,
      courseTeachers: course.courseTeachers.map(t => t.id),
      color: course.color,
      occurrences:
        course.occurrences === undefined
          ? []
          : course.occurrences.map(
              o =>
                ({
                  slot: o.slot,
                  weekday: dayToDayOfWeek(o.weekday),
                } as CourseOccurrenceAPI),
            ),
    });

    return courseResponseToClassCourse(result.data);
  },

  async createClassCourseFromCourseCreateEntry(
    authToken: string,
    organizationId: string,
    courseCreateEntry: CourseCreateEntry,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.createCourse(organizationId, courseCreateEntry);
    return result.data;
  },

  async removeClassCourse(
    authToken: string,
    organizationId: string,
    courseId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    /// swagger wrong deleteCourse returns string
    await api.deleteCourse(courseId, organizationId);
  },

  async getGeneralSettings(
    authToken: string,
    organizationId: string,
  ): Promise<GeneralSettingsItem> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getGeneralSettings(organizationId);

    return result.data;
  },

  async updateGeneralSettings(
    authToken: string,
    organizationId: string,
    settings: GeneralSettingsItem,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    await api.createOrUpdateGeneralSettings(organizationId, settings);
  },

  async getAllExcuseDefinitions(
    authToken: string,
    organizationId: string,
  ): Promise<ExcuseDefinitionResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAllAbsenceExcuseDefinitions(organizationId);

    return result.data;
  },

  async getReasonsForAbsence(
    authToken: string,
    organizationId: string,
  ): Promise<AbsenceReasonResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllAbsenceReasons(organizationId);

    return result.data;
  },
  async createReasonForAbsence(
    authToken: string,
    organizationId: string,
    absenceReasonEntry: AbsenceReasonEntry,
  ): Promise<AbsenceReasonResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.createAbsencesReasons(
      organizationId,
      absenceReasonEntry,
    );

    return result.data;
  },
  async updateReasonForAbsence(
    authToken: string,
    organizationId: string,
    absenceReasonPutEntry: AbsenceReasonPutEntry,
  ): Promise<AbsenceReasonResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateAbsenceReasons(
      organizationId,
      absenceReasonPutEntry,
    );

    return result.data;
  },
  async deleteReasonForAbsence(
    authToken: string,
    organizationId: string,
    absenceReasonId: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.deleteAbsenceReason(
      absenceReasonId,
      organizationId,
    );
  },

  /**
   * Returns all classes of the organization, including those of grades the
   * caller cannot read.
   */
  async getAllClasses(
    authToken: string,
    organizationId: string,
  ): Promise<ClassBasicResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicClassesOfSettings(organizationId);

    return result.data.value;
  },

  async getAllCourses(
    authToken: string,
    organizationId: string,
  ): Promise<CourseBasicResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicCoursesOfSettings(organizationId);

    return result.data.value;
  },

  async updateCoursesClassDependcies(
    authToken: string,
    organizationId: string,
    courseId: string,
    classesToAdd: string[],
    classesToRemove: string[],
  ): Promise<CourseResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.updateCoursesClassDependencies(organizationId, {
      courseId,
      classDependenciesToAdd: classesToAdd,
      classDependenciesToRemove: classesToRemove,
    });
    return result.data;
  },
};
